/**
 * Reusable React error boundary (SPEC_03).
 *
 * Before this existed a single thrown render error anywhere in the tree
 * white-screened the whole SPA. Three placements contain failures to the
 * smallest sensible surface:
 *   - app root (App.tsx): last line of defense, full-screen fallback;
 *   - per route (inside AppRoute/ProtectedRoute, keyed on pathname): a
 *     crash in one surface leaves navigation to every other surface alive;
 *   - per assistant message (PatProThread): one malformed model artifact
 *     degrades to an inline notice instead of blanking the transcript.
 *
 * Known limits (React contract, not this implementation): boundaries do NOT
 * catch errors thrown in event handlers, async code/promises, or outside
 * React's render phase. Those paths carry their own handling (e.g. the SSE
 * watchdog + stalled-turn banner in src/lib/chat/sse.ts / store.ts); the
 * global reporting hook for them arrives with SPEC_04 Task 4.
 *
 * Every caught error is console.error'd under the [ErrorBoundary] prefix —
 * a boundary that hides errors from operators is worse than the crash.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '../../lib/observability/report';

export type ErrorBoundaryFallback = (error: Error, reset: () => void) => ReactNode;

type Props = {
  children: ReactNode;
  /** Custom fallback renderer — theme per surface. Default: centered card. */
  fallback?: ErrorBoundaryFallback;
  /** Reporting hook (wired to SPEC_04's reportError when that lands). */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /**
   * When any entry changes (e.g. [location.pathname]), a held error is
   * cleared automatically so navigating away from a crashed surface works
   * without a manual reset.
   */
  resetKeys?: unknown[];
};

type State = {
  error: Error | null;
  // Counts consecutive failures since the last successful render so the
  // default fallback can escalate from soft reset to a hard page reload.
  failures: number;
};

function keysChanged(a: unknown[] | undefined, b: unknown[] | undefined): boolean {
  if (a === b) return false;
  if (!a || !b || a.length !== b.length) return true;
  return a.some((v, i) => !Object.is(v, b[i]));
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, failures: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState((s) => ({ failures: s.failures + 1 }));
    console.error('[ErrorBoundary] caught render error:', error, errorInfo.componentStack);
    // No-op unless VITE_ERROR_DSN is configured (SPEC_04 Task 4).
    reportError(error, { source: 'error-boundary' });
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.error && keysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <DefaultFallback
        // A reset that immediately re-throws means the error isn't
        // transient — escalate the affordance to a hard reload.
        escalate={this.state.failures > 1}
        onReset={this.reset}
      />
    );
  }
}

function DefaultFallback({
  escalate,
  onReset,
}: {
  escalate: boolean;
  onReset: () => void;
}) {
  return (
    <div className="flex items-center justify-center min-h-[50vh] p-6" role="alert">
      <div className="flex flex-col items-start gap-3 max-w-md px-6 py-5 bg-surface-white border border-border rounded-md shadow-sm">
        <h2 className="font-ui text-base font-semibold text-text-primary m-0">
          Something went wrong
        </h2>
        <p className="font-prose text-sm text-text-secondary m-0">
          This part of the app hit an unexpected error. Your work is saved on
          the server; {escalate ? 'reload the page to continue.' : 'try again, or reload the page.'}
        </p>
        <button
          type="button"
          className="inline-flex items-center px-3 py-1.5 font-ui text-sm text-surface-white bg-navy-500 border-0 rounded-sm cursor-pointer hover:bg-navy-700"
          onClick={() => {
            if (escalate) {
              window.location.reload();
            } else {
              onReset();
            }
          }}
        >
          {escalate ? 'Reload this page' : 'Try again'}
        </button>
      </div>
    </div>
  );
}
